import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading4 } from '@ui/Typography'

import { Box } from '@shared/components/Box'

import styles from './SwapFreeCountdown.module.scss'

interface Props {
  startRedZone: string | null
  daysTillSuspended: number
  isSuspended: boolean
}

export const SwapFreeCountdown: FC<Props> = ({ startRedZone, daysTillSuspended, isSuspended }) => {
  const { t } = useTranslation()
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  const MS_PER_SECOND = 1000
  const MS_PER_MINUTE = MS_PER_SECOND * 60
  const MS_PER_HOUR = MS_PER_MINUTE * 60
  const MS_PER_DAY = MS_PER_HOUR * 24

  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!startRedZone || isSuspended) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }

      const startDate = new Date(startRedZone)
      const suspensionDate = new Date(startDate)
      suspensionDate.setDate(suspensionDate.getDate() + daysTillSuspended)

      const now = new Date()
      const diffTime = Math.max(0, suspensionDate.getTime() - now.getTime())

      const days = Math.floor(diffTime / MS_PER_DAY)
      const hours = Math.floor((diffTime % MS_PER_DAY) / MS_PER_HOUR)
      const minutes = Math.floor((diffTime % MS_PER_HOUR) / MS_PER_MINUTE)
      const seconds = Math.floor((diffTime % MS_PER_MINUTE) / MS_PER_SECOND)

      return { days, hours, minutes, seconds }
    }

    setTimeRemaining(calculateTimeRemaining())

    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining())
    }, MS_PER_SECOND)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startRedZone, daysTillSuspended, isSuspended])

  if (isSuspended) {
    return null
  }

  const { days, hours, minutes, seconds } = timeRemaining

  const pad = (num: number) => String(num).padStart(2, '0')

  return (
    <Box flex justify='center'>
      <Box flex justify='center' gap='x16'>
        <Box flex direction='column' align='center'>
          <Box className={styles.timerBox}>
            <Heading4 medium>{pad(days)}</Heading4>
          </Box>
          <Body1>{t('Swap Free.Countdown.Day')}</Body1>
        </Box>
        <Box flex pt='x8'></Box>
        <Box flex direction='column' align='center'>
          <Box className={styles.timerBox}>
            <Heading4 medium>{pad(hours)}</Heading4>
          </Box>
          <Body1>{t('Swap Free.Countdown.Hour')}</Body1>
        </Box>
        <Box flex pt='x8'>
          <Heading4 medium>:</Heading4>
        </Box>
        <Box flex direction='column' align='center'>
          <Box className={styles.timerBox}>
            <Heading4 medium>{pad(minutes)}</Heading4>
          </Box>
          <Body1>{t('Swap Free.Countdown.Min')}</Body1>
        </Box>
        <Box flex direction='column' align='center'>
          <Box className={styles.timerBox}>
            <Heading4 medium>{pad(seconds)}</Heading4>
          </Box>
          <Body1>{t('Swap Free.Countdown.Sec')}</Body1>
        </Box>
      </Box>
    </Box>
  )
}
